import type { MetricFormat } from '@nono/contract';

/*
 * Single source of truth for rendering numbers, in nb-NO.
 *
 * Wire conventions (verified against apps/api/src/dashboard/metrics.ts):
 *   - 'pct'  values are ALREADY in percent units, 1 decimal (pct1 → e.g. 27.3 = 27,3 %).
 *   - 'ratio' values are a plain multiplier (round2 → e.g. 2.45 = 2,45×).
 *   - 'nok'/'aed' are money as a 2dp number; we show whole kr in tables.
 *   - null = a guarded/zero-denominator value → renders the em dash, never 0/NaN.
 */

const EM_DASH = '—';

const intFmt = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });
const moneyFmt = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });
// Small costs (CPC/CPL/CPM) keep 1 decimal so kr 42,5 doesn't read as kr 43 (legacy parity).
const moneySmallFmt = new Intl.NumberFormat('nb-NO', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
function money(value: number): string {
  return value !== 0 && Math.abs(value) < 100
    ? moneySmallFmt.format(value)
    : moneyFmt.format(value);
}
const money2Fmt = new Intl.NumberFormat('nb-NO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const pct1Fmt = new Intl.NumberFormat('nb-NO', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const ratioFmt = new Intl.NumberFormat('nb-NO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Non-breaking space between a value and its unit so "2,3 %" / "189 kr" never
// wrap onto two lines in a dense cell.
const NBSP = String.fromCharCode(0xa0);

/** Format a metric value by its declared MetricDef.format. null → em dash. */
export function formatMetric(value: number | null | undefined, format: MetricFormat): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  switch (format) {
    case 'int':
      return intFmt.format(value);
    case 'nok':
      return `${money(value)}${NBSP}kr`;
    case 'aed':
      return `${money(value)}${NBSP}AED`;
    case 'pct':
      return `${pct1Fmt.format(value)}${NBSP}%`;
    case 'ratio':
      return `${ratioFmt.format(value)}×`;
    default:
      return String(value);
  }
}

/** Compact money for headline cards (1 234 567 → 1,23 mill). */
export function formatMoneyCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  if (Math.abs(value) >= 1_000_000) return `${ratioFmt.format(value / 1_000_000)} mill kr`;
  if (Math.abs(value) >= 10_000) return `${intFmt.format(value)} kr`;
  return `${money2Fmt.format(value)} kr`;
}

/** Signed percentage-point or relative delta, for trend chips. */
export function formatDelta(value: number | null | undefined, format: MetricFormat): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatMetric(value, format === 'nok' || format === 'aed' ? 'int' : format)}`;
}

// Parse at noon UTC so the weekday never drifts across timezones (legacy parity).
const weekdayFmt = new Intl.DateTimeFormat('nb-NO', { weekday: 'short', timeZone: 'UTC' });

/** 'YYYY-MM-DD' → { weekday: 'man', date: 'DD.MM.YY' }. */
export function formatIsoDate(iso: string): { weekday: string; date: string } {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return { weekday: '', date: iso };
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  const dd = String(d).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const yy = String(y).slice(2);
  return { weekday: weekdayFmt.format(dt).replace(/\.$/, ''), date: `${dd}.${mm}.${yy}` };
}

// Booking timestamps render in the gym's timezone, not the viewer's — the day
// must agree with the Oslo-bucketed `date` the backend computed.
const bookedAtFmt = new Intl.DateTimeFormat('nb-NO', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Europe/Oslo',
});

/** ISO timestamp → 'dd.MM HH:mm' in Europe/Oslo (e.g. '11.06 14:32'). */
export function formatBookedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const parts = bookedAtFmt.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}.${get('month')} ${get('hour')}:${get('minute')}`;
}

const SOURCE_LABEL: Record<string, string> = {
  meta: 'Meta',
  momence: 'Momence',
  stripe: 'Stripe',
  derived: 'Avledet',
};

/** Humanize a backend metric `desc` for a tooltip: strip raw Meta event tokens
 * (dev leakage) and append a human source. */
export function metricTooltip(desc: string, source: string): string {
  const clean = desc
    .replace(/conversions\.schedule_website|schedule_website/g, 'Meta nettside-bookinger')
    .replace(/onsite_conversion\.lead_grouped/g, 'Meta instant-form leads');
  return `${clean} · Kilde: ${SOURCE_LABEL[source] ?? source}`;
}
